import React from 'react';
import { Info, AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { requestCollateralLimitUpgrade } from '../../utils/contactUtils';

/**
 * UsageStatsCard Component
 * Displays usage statistics with progress bars and visual indicators
 * 
 * @param {Object} props
 * @param {Object} props.stats - Usage statistics object
 * @param {Object} props.stats.collaterals - Collateral usage stats
 * @param {number} props.stats.collaterals.allowed - Total allowed collaterals
 * @param {number} props.stats.collaterals.created - Number of created collaterals
 * @param {number} props.stats.collaterals.remaining - Remaining collaterals
 * @param {number} props.stats.collaterals.percentage - Usage percentage
 * @param {Object} props.stats.editsPerCollateral - Edit limits
 * @param {number} props.stats.editsPerCollateral.allowed - Allowed edits per collateral
 * @param {string} props.type - Type of card: 'collateral-create' | 'collateral-edit'
 * @param {number} props.currentVersions - Current version count (for edit cards)
 */
export default function UsageStatsCard({ stats, type = 'collateral-create', currentVersions = 0 }) {
  if (!stats) return null;

  const isCreateCard = type === 'collateral-create';
  const isEditCard = type === 'collateral-edit';

  // Determine status color based on usage
  const getStatusColor = (percentage) => {
    if (percentage >= 90) return '#FF6B5A'; // Red - critical
    if (percentage >= 70) return '#FFA500'; // Orange - warning
    return '#10B981'; // Green - good
  };

  const getEditStatusColor = (current, allowed) => {
    const percentage = (current / allowed) * 100;
    if (percentage >= 90) return '#FF6B5A';
    if (percentage >= 70) return '#FFA500';
    return '#10B981';
  };

  if (isCreateCard) {
    const { allowed, created, remaining, percentage } = stats.collaterals;
    // Reverse the percentage to show remaining instead of used
    const remainingPercentage = ((remaining / allowed) * 100);
    const statusColor = getStatusColor(percentage);
    const isNearLimit = percentage >= 70;
    const isAtLimit = remaining === 0;

    return (
      <div 
        className="rounded-xl border p-6 shadow-sm transition-all"
        style={{ 
          backgroundColor: '#F8F7F4', 
          borderColor: isAtLimit ? '#FF6B5A' : '#D7B472'
        }}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${statusColor}20` }}
            >
              {isAtLimit ? (
                <AlertCircle className="h-5 w-5" style={{ color: statusColor }} />
              ) : isNearLimit ? (
                <Info className="h-5 w-5" style={{ color: statusColor }} />
              ) : (
                <CheckCircle className="h-5 w-5" style={{ color: statusColor }} />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>
                Collateral Usage
              </h3>
              <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                Your current plan limits
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#0A0A10' }}>
              Collaterals Remaining
            </span>
            <span className="text-2xl font-bold" style={{ color: statusColor }}>
              {remaining}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm mb-3" style={{ color: '#0A0A10', opacity: 0.7 }}>
            <span>{remaining} of {allowed} remaining</span>
            <span>{Math.round(remainingPercentage)}%</span>
          </div>

          {/* Progress bar - reversed to show remaining credits */}
          <div className="h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
            <div 
              className="h-full transition-all duration-500 rounded-full"
              style={{ 
                width: `${remainingPercentage}%`,
                backgroundColor: statusColor
              }}
            />
          </div>
        </div>

        {/* Warning message */}
        {isAtLimit && (
          <div 
            className="rounded-lg p-3 text-sm"
            style={{ backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}
          >
            <div className="flex items-start gap-2 mb-3">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold mb-1">Limit Reached</p>
                <p>You've reached your collateral creation limit. Please contact your team to upgrade your plan.</p>
              </div>
            </div>
            <button
              onClick={requestCollateralLimitUpgrade}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium text-sm transition-all hover:shadow-md w-full justify-center"
              style={{ backgroundColor: '#FF6B5A' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
            >
              <Mail className="h-4 w-4" />
              Contact Team to Upgrade
            </button>
          </div>
        )}

        {isNearLimit && !isAtLimit && (
          <div 
            className="rounded-lg p-3 text-sm flex items-start gap-2"
            style={{ backgroundColor: 'rgba(255, 165, 0, 0.1)', color: '#FFA500' }}
          >
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Approaching Limit</p>
              <p>You're approaching your collateral creation limit. Consider upgrading your plan.</p>
            </div>
          </div>
        )}

        {!isNearLimit && (
          <div 
            className="rounded-lg p-3 text-sm flex items-start gap-2"
            style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}
          >
            <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">You can create {remaining} more collateral{remaining !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (isEditCard) {
    const { allowed } = stats.editsPerCollateral;
    const remaining = Math.max(0, allowed - currentVersions);
    const percentage = (currentVersions / allowed) * 100;
    const statusColor = getEditStatusColor(currentVersions, allowed);
    const isNearLimit = percentage >= 70;
    const isAtLimit = remaining === 0;

    return (
      <div 
        className="rounded-xl border p-6 shadow-sm transition-all"
        style={{ 
          backgroundColor: '#F8F7F4', 
          borderColor: isAtLimit ? '#FF6B5A' : '#D7B472'
        }}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${statusColor}20` }}
            >
              {isAtLimit ? (
                <AlertCircle className="h-5 w-5" style={{ color: statusColor }} />
              ) : isNearLimit ? (
                <Info className="h-5 w-5" style={{ color: statusColor }} />
              ) : (
                <CheckCircle className="h-5 w-5" style={{ color: statusColor }} />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>
                Edit Limit
              </h3>
              <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                Remaining edits for this collateral
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#0A0A10' }}>
              Edits Remaining
            </span>
            <span className="text-2xl font-bold" style={{ color: statusColor }}>
              {remaining}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm mb-3" style={{ color: '#0A0A10', opacity: 0.7 }}>
            <span>Version {currentVersions} of {allowed}</span>
            <span>{Math.round(percentage)}%</span>
          </div>

          {/* Progress bar */}
          <div className="h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
            <div 
              className="h-full transition-all duration-500 rounded-full"
              style={{ 
                width: `${percentage}%`,
                backgroundColor: statusColor
              }}
            />
          </div>
        </div>

        {/* Warning message */}
        {isAtLimit && (
          <div 
            className="rounded-lg p-3 text-sm"
            style={{ backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}
          >
            <div className="flex items-start gap-2 mb-3">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold mb-1">Edit Limit Reached</p>
                <p>You've reached the maximum number of edits for this collateral. Contact your team to upgrade.</p>
              </div>
            </div>
            <button
              onClick={requestCollateralLimitUpgrade}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium text-sm transition-all hover:shadow-md w-full justify-center"
              style={{ backgroundColor: '#FF6B5A' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
            >
              <Mail className="h-4 w-4" />
              Contact Team to Upgrade
            </button>
          </div>
        )}

        {isNearLimit && !isAtLimit && (
          <div 
            className="rounded-lg p-3 text-sm flex items-start gap-2"
            style={{ backgroundColor: 'rgba(255, 165, 0, 0.1)', color: '#FFA500' }}
          >
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Approaching Edit Limit</p>
              <p>You have {remaining} edit{remaining !== 1 ? 's' : ''} remaining for this collateral.</p>
            </div>
          </div>
        )}

        {!isNearLimit && (
          <div 
            className="rounded-lg p-3 text-sm flex items-start gap-2"
            style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}
          >
            <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">You can make {remaining} more edit{remaining !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

